import { useEffect, useState } from 'react';
import { publishersManageService } from '../../../api/publishersManageService';
import { useBulkPublisherActions } from '../../../hooks/useBulkPublisherActions';
import type { BulkPublisherOperationResultDto } from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  open: boolean;
  publisherIds: string[];
  onClose: () => void;
  onCompleted: (result: BulkPublisherOperationResultDto) => void;
}

export default function PublisherDeleteConfirmDialog({
  open, publisherIds, onClose, onCompleted,
}: Props) {
  const { t } = useTranslation();
  const [alsoDeleteBooks, setAlsoDeleteBooks] = useState(true);
  const [bookCount, setBookCount] = useState<number | null>(null);
  const { bulkDelete } = useBulkPublisherActions();

  useEffect(() => {
    if (!open) return;
    setAlsoDeleteBooks(true);
    setBookCount(null);
    publishersManageService.affectedBookCount(publisherIds)
      .then(setBookCount)
      .catch(() => setBookCount(null));
  }, [open, publisherIds]);

  if (!open) return null;

  const submit = async () => {
    const result = await bulkDelete.mutateAsync({ publisherIds, alsoDeleteBooks });
    onCompleted(result);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
        <h2 className="text-lg font-semibold mb-2 text-red-700">
          {t('App.Admin.Publishers.PublisherDeleteConfirmDialog.Title', { count: publisherIds.length })}
        </h2>
        <p className="text-sm text-neutral-700 mb-4">{t('App.Admin.Publishers.PublisherDeleteConfirmDialog.Description')}</p>

        <label className="flex items-start gap-2 text-sm mb-4">
          <input
            type="checkbox"
            checked={alsoDeleteBooks}
            onChange={(e) => setAlsoDeleteBooks(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            {t('App.Admin.Publishers.PublisherDeleteConfirmDialog.AlsoDeleteBooks')}
            {bookCount !== null && <span className="text-neutral-500"> {t('App.Admin.Publishers.PublisherDeleteConfirmDialog.AffectedBooksCount', { count: bookCount })}</span>}
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100">{t('App.Admin.Publishers.PublisherDeleteConfirmDialog.Cancel')}</button>
          <button
            type="button"
            onClick={submit}
            disabled={bulkDelete.isPending}
            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {t('App.Admin.Publishers.PublisherDeleteConfirmDialog.Delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
